import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Divider,
    Grid,
    IconButton,
    Collapse,
    Paper,
    TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FormProvider, useForm } from 'react-hook-form';
import { IsSmallScreen } from '../../commonFunctions/CommonFunctions';

interface IFilterSearchTextProps {
    rows: IRows;
    onFilterChange: (filters: IFilter[]) => void;
    alignment?: 'left' | 'center' | 'right';
    defaultFilter?: any[];
}

export interface IRows {
    fieldName?: string;
}

interface IFilter {
    field: string;
    operator: Operator | string;
    value: string | number;
}

export enum Operator {
    EqualTo = 'eq',
    NotEqualTo = 'neq',
    GreaterThan = 'gt',
    LessThan = 'lt',
    Like = 'like',
}

export const FilterSearchText: React.FC<IFilterSearchTextProps> = React.memo(
    ({ rows, onFilterChange, alignment = 'left', defaultFilter }) => {
        const methods = useForm();
        const { register, handleSubmit, control, reset, getValues } = methods;
        const isMobile = IsSmallScreen();

        const [showSearch, setShowSearch] = useState(false);
        const wrapperRef = useRef<HTMLDivElement | null>(null);

        // 🔄 Toggle search box visibility
        const toggleSearch = () => {
            const searchText = getValues('searchAll');
            if (showSearch && searchText && searchText.trim() !== '') {
                return; // Don't collapse if there's text
            }
            setShowSearch((prev) => !prev);
        };

        // 🔁 Collapse when clicking outside (only if input is empty)
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                const inputValue = getValues('searchAll');
                if (
                    wrapperRef.current &&
                    !wrapperRef.current.contains(event.target as Node)
                ) {
                    if (!inputValue || inputValue.trim() === '') {
                        setShowSearch(false);
                    }
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [getValues]);

        const handleReset = () => {
            reset();
            onFilterChange([]);
        };

        const onSubmit = (data: Record<string, any>) => {
            if (data.searchAll === undefined) {
                handleReset();
            }

            if (!rows || !rows.fieldName) {
                console.warn('No field names found in rows.');
                return;
            }

            const appliedFilters: IFilter[] = [];

            if (defaultFilter && defaultFilter.length > 0) {
                appliedFilters.push(...defaultFilter);
            }

            if (data.searchAll && data.searchAll !== '') {
                appliedFilters.push({
                    field: rows.fieldName,
                    operator: Operator.Like,
                    value: data.searchAll,
                });
            }

            onFilterChange(appliedFilters);
        };

        return (
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Divider sx={{ marginLeft: 1 }} />

                    <Grid container spacing={2} sx={{ margin: 0, alignItems: 'center' }}>
                        <Grid item>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingX: 1,
                                    paddingY: 0.25,
                                    backgroundColor: showSearch ? '#fff' : 'transparent',
                                    boxShadow: 'none',
                                }}
                                ref={wrapperRef}
                            >
                                <IconButton
                                    onClick={toggleSearch}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            //backgroundColor: 'white',
                                            opacity:'0.75'
                                        },
                                        padding: 0.5,
                                    }}
                                >
                                    <SearchIcon sx={{color:showSearch?"inherit":'white'}} />
                                </IconButton>

                                <Collapse in={showSearch} orientation="horizontal" timeout={300}>
                                    <Box sx={{ width: 200, ml: 1 }}>
                                        <TextField
                                            fullWidth
                                            id="searchAll"
                                            size="small"
                                            {...register('searchAll')}
                                            name="searchAll"
                                            placeholder={showSearch?"Search...":""}
                                            InputProps={{ disableUnderline: true }}
                                            variant="standard"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSubmit(onSubmit)();
                                                    e.preventDefault();
                                                }
                                            }}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    paddingY: '6px',
                                                    fontSize: '0.875rem',
                                                },
                                              //  backgroundColor: 'white'
                                            }}
                                        />
                                    </Box>
                                </Collapse>
                            </Paper>
                        </Grid>
                    </Grid>
                </form>
            </FormProvider>
        );
    }
);
